import { useMemo, type FC } from 'react';
import { Button, Select, Input, Collapse, Upload, message } from 'antd';
import { Sparkles, Upload as UploadIcon, Loader2, Settings, RefreshCw } from 'lucide-react';

const MODEL_OPTIONS = [
  { value: 'gemini-2.5-flash-image', label: '🎨 Gemini 2.5 Flash Image (recommandé)' },
  { value: 'gemini-3-pro-image-preview', label: '💎 Gemini 3 Pro Image (haute qualité)' },
];

const RATIO_OPTIONS = [
  { value: '16:9', label: '16:9 (blog)' },
  { value: '1:1', label: '1:1 (carré)' },
  { value: '4:3', label: '4:3' },
  { value: '3:2', label: '3:2' },
  { value: '9:16', label: '9:16 (portrait)' },
  { value: '21:9', label: '21:9 (ultra-wide)' },
];

const SIZE_OPTIONS = [
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K (Pro)' },
  { value: '4K', label: '4K (Pro)' },
];

/** Token cost estimation by model & size. */
const TOKEN_ESTIMATE: Record<string, Record<string, number>> = {
  'gemini-2.5-flash-image': { '1K': 1120, '2K': 1120, '4K': 2000 },
  'gemini-3-pro-image-preview': { '1K': 1120, '2K': 1120, '4K': 2000 },
};

type Mode = 'generate' | 'upload';

interface ImageGenerateControlsProps {
  mode: Mode;
  generating: boolean;
  model: string;
  aspectRatio: string;
  imageSize: string;
  styleHint: string;
  previewUrl: string | null;
  articleTitle: string;
  seoKeywords: string[];
  totalGenerations: number;
  totalTokensUsed: number;
  onModelChange: (value: string) => void;
  onAspectRatioChange: (value: string) => void;
  onImageSizeChange: (value: string) => void;
  onStyleHintChange: (value: string) => void;
  onGenerate: () => void;
  onLocalImageLoad: (url: string) => void;
}

const ImageGenerateControls: FC<ImageGenerateControlsProps> = ({
  mode,
  generating,
  model,
  aspectRatio,
  imageSize,
  styleHint,
  previewUrl,
  articleTitle,
  seoKeywords,
  totalGenerations,
  totalTokensUsed,
  onModelChange,
  onAspectRatioChange,
  onImageSizeChange,
  onStyleHintChange,
  onGenerate,
  onLocalImageLoad,
}) => {
  const estimatedTokens = useMemo(() => TOKEN_ESTIMATE[model]?.[imageSize] ?? 1120, [model, imageSize]);

  const defaultPromptPreview = useMemo(() => {
    const kw = seoKeywords.length > 0 ? seoKeywords.join(', ') : 'SaaS, product intelligence';
    return `Topic: ${articleTitle || 'Untitled'}\nKeywords: ${kw}`;
  }, [articleTitle, seoKeywords]);

  if (mode === 'generate') {
    return (
      <div className="flex-1 space-y-3">
        {/* Prompt preview */}
        <div className="rounded-md bg-slate-50 p-2 dark:bg-slate-700/50">
          <p className="mb-1 text-[10px] font-medium uppercase text-slate-400 dark:text-slate-500">Prompt auto</p>
          <p className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-line">{defaultPromptPreview}</p>
        </div>

        {/* Advanced settings */}
        <Collapse
          ghost
          size="small"
          items={[
            {
              key: 'advanced',
              label: (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  <Settings className="mr-1 inline h-3 w-3" />
                  Paramètres avancés
                </span>
              ),
              children: (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-0.5 block text-[10px] text-slate-500 dark:text-slate-400">Modèle</label>
                    <Select value={model} onChange={onModelChange} options={MODEL_OPTIONS} className="w-full" size="small" />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-[10px] text-slate-500 dark:text-slate-400">Ratio</label>
                    <Select value={aspectRatio} onChange={onAspectRatioChange} options={RATIO_OPTIONS} className="w-full" size="small" />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-[10px] text-slate-500 dark:text-slate-400">Taille</label>
                    <Select value={imageSize} onChange={onImageSizeChange} options={SIZE_OPTIONS} className="w-full" size="small" />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-[10px] text-slate-500 dark:text-slate-400">Style hint</label>
                    <Input value={styleHint} onChange={(e) => onStyleHintChange(e.target.value)} placeholder="isometric 3D..." size="small" />
                  </div>
                </div>
              ),
            },
          ]}
        />

        {/* Generate button */}
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            onClick={onGenerate}
            disabled={generating}
            icon={
              generating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : previewUrl ? (
                <RefreshCw className="h-3.5 w-3.5" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )
            }
            className="!bg-gradient-to-r !from-blue-600 !to-violet-600 !border-0 !text-white hover:!opacity-90"
          >
            {generating ? 'Génération...' : previewUrl ? `Re-générer — ~${estimatedTokens} tkn` : `Générer — ~${estimatedTokens} tkn`}
          </Button>

          {totalGenerations > 0 && (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              {totalGenerations} génération{totalGenerations > 1 ? 's' : ''} · {totalTokensUsed} tokens
            </span>
          )}
        </div>
      </div>
    );
  }

  /* Upload mode */
  return (
    <div className="flex-1 space-y-3">
      <Upload.Dragger
        accept="image/*"
        showUploadList={false}
        customRequest={({ file, onSuccess }) => {
          const reader = new FileReader();
          reader.onload = () => {
            const url = reader.result as string;
            onLocalImageLoad(url);
            onSuccess?.({});
            message.info('Image chargée localement. Sauvegardez pour uploader.');
          };
          reader.readAsDataURL(file as Blob);
        }}
        className="!border-dashed"
      >
        <div className="py-4 text-center">
          <UploadIcon className="mx-auto mb-2 h-6 w-6 text-slate-400" />
          <p className="text-xs text-slate-500 dark:text-slate-400">Glissez une image ou cliquez pour parcourir</p>
        </div>
      </Upload.Dragger>
    </div>
  );
};

export default ImageGenerateControls;
